import type { FetchResponse, RequestParams } from 'services/fetch';
import fetch, { ContentType } from 'services/fetch';

import type { AuthorizationTokenResponse } from './data-contracts';

/**
 * @description Support
*
 * @tags Support
 * @name CreateSupportChatAuthorizationToken
 * @request POST:/gw/inner/v1/support-chat/auth/token
 * @secure
 * @response `200` `AuthorizationTokenResponse` Returned in case request was successful
 * @response `400` `{
  \** @example "problems/validation-error" *\
    type: string,
  \** @example "Validation failed" *\
    title: string,
    fields?: ({
  \** @example "90f5c6c9-ba85-453b-9f6e-a1b8b01b2464" *\
    code?: string,
  \** @example "Validation failed" *\
    message?: string,
  \** @example "field_path" *\
    path?: string,

})[] | null,

}`
 * @response `401` `void`
 * @response `403` `{
  \** @example "Forbidden" *\
    errorMessage: string,
  \** @example 403 *\
    errorCode: number,
  \** @example "ERROR" *\
    status: string,
  \** @example "" *\
    data: string,

}`
 * @response `404` `any`
 * @response `429` `void`
 * @response `500` `{
    requestParams: object,
  \** @default "ERROR" *\
    status: "OK" | "ERROR",
      \**
          * @format int32
          * @example 0
          *\
    errorCode: number | null,
  \** @example "Error message" *\
    errorMessage: string | null,
    data: object,

}`

*/
export const createSupportChatAuthorizationToken = (
  params: RequestParams = {}
): FetchResponse<AuthorizationTokenResponse> => {
  return fetch<AuthorizationTokenResponse>(`/gw/inner/v1/support-chat/auth/token`, {
    method: 'POST',
    responseType: 'json',
    ...params,
  });
};

/**
 * No description
*
 * @tags Support
 * @name SendSupportChatRating
 * @summary Stores support chat ratings
 * @request POST:/gw/inner/v2/support-chat/rating
 * @secure
 * @response `204` `any`
 * @response `400` `{
  \** @example "problems/validation-error" *\
    type: string,
  \** @example "Validation failed" *\
    title: string,
    fields?: ({
  \** @example "90f5c6c9-ba85-453b-9f6e-a1b8b01b2464" *\
    code?: string,
  \** @example "Validation failed" *\
    message?: string,
  \** @example "field_path" *\
    path?: string,

})[] | null,

}`
 * @response `401` `{
  \** @example "access_denied" *\
    error?: string,
      \**
          * copy of "message"
          * @example "The resource owner or authorization server denied the request."
          *\
    error_description?: string,
  \** @example "Missing "Authorization" header" *\
    hint?: string | null,
      \**
          * Deprecated, use error_description instead.
          * @deprecated
          * @example "The resource owner or authorization server denied the request."
          *\
    message?: string,

}` Unauthorized error message
 * @response `403` `{
  \** @example "Forbidden" *\
    errorMessage: string,
  \** @example 403 *\
    errorCode: number,
  \** @example "ERROR" *\
    status: string,
  \** @example "" *\
    data: string,

}`
 * @response `404` `any`
 * @response `429` `{
  \** @example "Request limit reached." *\
    error?: string,

}` Too many requests error message.
 * @response `500` `{
    requestParams: object,
  \** @default "ERROR" *\
    status: "OK" | "ERROR",
      \**
          * @format int32
          * @example 0
          *\
    errorCode: number | null,
  \** @example "Error message" *\
    errorMessage: string | null,
    data: object,

}`

*/
export const sendSupportChatRating = (
  data: {
    adminName: string;
    /**
     * @min 1
     * @max 4
     */
    ratingValue: number;
    ratingText: string;
    conversationId: string;
  },
  params: RequestParams = {}
): FetchResponse<any> => {
  return fetch<any>(`/gw/inner/v2/support-chat/rating`, {
    method: 'POST',
    body: data,
    contentType: ContentType.Json,
    ...params,
  });
};

/**
 * @description Support
*
 * @tags Support
 * @name CreateSupportChatGuestAuthorizationToken
 * @request POST:/public-gw/inner/v1/support-chat/guest-auth/token
 * @secure
 * @response `200` `AuthorizationTokenResponse` Returned in case request was successful
 * @response `400` `{
  \** @example "problems/validation-error" *\
    type: string,
  \** @example "Validation failed" *\
    title: string,
    fields?: ({
  \** @example "90f5c6c9-ba85-453b-9f6e-a1b8b01b2464" *\
    code?: string,
  \** @example "Validation failed" *\
    message?: string,
  \** @example "field_path" *\
    path?: string,

})[] | null,

}`
 * @response `401` `void`
 * @response `403` `{
  \** @example "Forbidden" *\
    errorMessage: string,
  \** @example 403 *\
    errorCode: number,
  \** @example "ERROR" *\
    status: string,
  \** @example "" *\
    data: string,

}`
 * @response `404` `any`
 * @response `429` `void`
 * @response `500` `{
    requestParams: object,
  \** @default "ERROR" *\
    status: "OK" | "ERROR",
      \**
          * @format int32
          * @example 0
          *\
    errorCode: number | null,
  \** @example "Error message" *\
    errorMessage: string | null,
    data: object,

}`

*/
export const createSupportChatGuestAuthorizationToken = (
  data: {
    /** @min 3 */
    guestNick: string;
  },
  params: RequestParams = {}
): FetchResponse<AuthorizationTokenResponse> => {
  return fetch<AuthorizationTokenResponse>(`/public-gw/inner/v1/support-chat/guest-auth/token`, {
    method: 'POST',
    body: data,
    responseType: 'json',
    ...params,
  });
};
